import React, { useState, useEffect } from 'react';
import './CredentialManager.css';

const CredentialManager = ({ onCredentialChange }) => {
    const [credentials, setCredentials] = useState([]);
    const [activeUser, setActiveUser] = useState('default');
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCredential, setNewCredential] = useState({
        name: '',
        apiKey: '',
        apiSecret: '',
        isDemo: true
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    // Load credentials on component mount
    useEffect(() => {
        loadCredentials();
    }, []);

    const loadCredentials = async () => {
        try {
            const response = await fetch('/api/credentials/list');
            const data = await response.json();
            if (data.credentials) {
                setCredentials(data.credentials);
                const active = data.credentials.find(c => c.isActive);
                if (active) {
                    setActiveUser(active.id);
                }
            }
        } catch (error) {
            console.error('Error loading credentials:', error);
            setMessage('Error loading credentials');
        }
    };

    const addCredential = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');

        try {
            const response = await fetch('/api/credentials/add', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newCredential)
            });

            const data = await response.json();
            
            if (data.success) {
                setMessage('✅ Credentials added successfully!');
                setNewCredential({ name: '', apiKey: '', apiSecret: '', isDemo: true });
                setShowAddForm(false);
                loadCredentials();
            } else {
                setMessage(`❌ Error: ${data.error}`);
            }
        } catch (error) {
            console.error('Error adding credentials:', error);
            setMessage('❌ Error adding credentials');
        } finally {
            setLoading(false);
        }
    };

    const switchCredential = async (userId) => {
        setLoading(true);
        setMessage('');

        try {
            const response = await fetch('/api/credentials/switch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId })
            });

            const data = await response.json();
            
            if (data.success) {
                setActiveUser(userId);
                setMessage(`✅ Switched to ${data.message}`);
                if (onCredentialChange) {
                    onCredentialChange(userId);
                }
            } else {
                setMessage(`❌ Error: ${data.error}`);
            }
        } catch (error) {
            console.error('Error switching credentials:', error);
            setMessage('❌ Error switching credentials');
        } finally {
            setLoading(false);
        }
    };

    const deleteCredential = async (userId) => {
        if (userId === 'default') {
            setMessage('❌ Cannot delete default account');
            return;
        }

        if (!window.confirm('Are you sure you want to delete these credentials?')) {
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await fetch('/api/credentials/delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId })
            });

            const data = await response.json();
            
            if (data.success) {
                setMessage('✅ Credentials deleted successfully!');
                loadCredentials();
                if (activeUser === userId) {
                    setActiveUser('default');
                }
            } else {
                setMessage(`❌ Error: ${data.error}`);
            }
        } catch (error) {
            console.error('Error deleting credentials:', error);
            setMessage('❌ Error deleting credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="credential-manager">
            <div className="credential-header">
                <h3>👥 Account Management</h3>
                <button 
                    className="add-credential-btn"
                    onClick={() => setShowAddForm(!showAddForm)}
                >
                    {showAddForm ? 'Cancel' : '+ Add Account'}
                </button>
            </div>

            {message && (
                <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
                    {message}
                </div>
            )}

            {showAddForm && (
                <div className="add-credential-form">
                    <h4>Add New Account</h4>
                    <form onSubmit={addCredential}>
                        <div className="form-group">
                            <label>Account Name:</label>
                            <input
                                type="text"
                                value={newCredential.name}
                                onChange={(e) => setNewCredential({...newCredential, name: e.target.value})}
                                placeholder="e.g., John's Account"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>API Key:</label>
                            <input
                                type="text"
                                value={newCredential.apiKey}
                                onChange={(e) => setNewCredential({...newCredential, apiKey: e.target.value})}
                                placeholder="Your Bybit API Key"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>API Secret:</label>
                            <input
                                type="password"
                                value={newCredential.apiSecret}
                                onChange={(e) => setNewCredential({...newCredential, apiSecret: e.target.value})}
                                placeholder="Your Bybit API Secret"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={newCredential.isDemo}
                                    onChange={(e) => setNewCredential({...newCredential, isDemo: e.target.checked})}
                                />
                                Demo Account (Testnet)
                            </label>
                        </div>
                        <div className="form-actions">
                            <button type="submit" disabled={loading}>
                                {loading ? 'Adding...' : 'Add Account'}
                            </button>
                            <button type="button" onClick={() => setShowAddForm(false)}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="credentials-list">
                <h4>Available Accounts</h4>
                {credentials.map(cred => (
                    <div key={cred.id} className={`credential-item ${cred.isActive ? 'active' : ''}`}>
                        <div className="credential-info">
                            <div className="credential-name">
                                {cred.name} {cred.isActive && '👑'}
                            </div>
                            <div className="credential-details">
                                {cred.isDemo ? '🧪 Demo' : '💰 Live'} • 
                                Created: {new Date(cred.createdAt).toLocaleDateString()}
                            </div>
                        </div>
                        <div className="credential-actions">
                            {!cred.isActive && (
                                <button 
                                    className="switch-btn"
                                    onClick={() => switchCredential(cred.id)}
                                    disabled={loading}
                                >
                                    Switch
                                </button>
                            )}
                            {cred.id !== 'default' && (
                                <button 
                                    className="delete-btn"
                                    onClick={() => deleteCredential(cred.id)}
                                    disabled={loading}
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="current-account">
                <strong>Current Account:</strong> {
                    credentials.find(c => c.id === activeUser)?.name || 'Default Account'
                }
            </div>
        </div>
    );
};

export default CredentialManager;
